import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Calendar, Clock, Phone, Video, Building2, CheckCircle, XCircle,
  AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, subDays } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const APPOINTMENT_TYPES = [
  { value: 'Office Visit', label: 'Office Visit', icon: Building2 },
  { value: 'Zoom Consultation', label: 'Zoom Consultation', icon: Video },
  { value: 'Phone Consultation', label: 'Phone Consultation', icon: Phone },
];

const STATUS_CONFIG = {
  Scheduled:   { color: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
  Completed:   { color: 'bg-emerald-100 text-emerald-800', label: 'Completed' },
  'No Show':   { color: 'bg-orange-100 text-orange-800', label: 'No Show' },
  Cancelled:   { color: 'bg-slate-100 text-slate-500', label: 'Cancelled' },
  Rescheduled: { color: 'bg-violet-100 text-violet-800', label: 'Rescheduled' },
};

const getTypeIcon = (type) => {
  const found = APPOINTMENT_TYPES.find(t => t.value === type);
  return found?.icon || Calendar;
};

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, appointment: null, action: null });
  const [actionLoading, setActionLoading] = useState(false);

  // Reschedule modal state
  const [rescheduleDialog, setRescheduleDialog] = useState({ open: false, appointment: null });
  const [rescheduleForm, setRescheduleForm] = useState({ date: '', time: '', type: '', notes: '' });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  const { hasPermission, role } = usePermissions();
  const canUpdate = hasPermission('leads_update_own') || hasPermission('leads_update_any');
  const isReadOnly = role === 'finance' || role === 'top_management';

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments?date=${selectedDate}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // ---- Status Actions ----
  const handleStatusUpdate = async (appointmentId, newStatus) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Appointment marked as ${newStatus}`);
        fetchAppointments();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to update appointment');
      }
    } catch {
      toast.error('Failed to update appointment');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ open: false, appointment: null, action: null });
    }
  };

  // ---- Reschedule ----
  const openReschedule = (appointment) => {
    const startDt = appointment.start_datetime ? new Date(appointment.start_datetime) : new Date();
    const nextDay = addDays(new Date(), 1);
    setRescheduleForm({
      date: nextDay.toISOString().split('T')[0],
      time: `${String(startDt.getHours()).padStart(2, '0')}:${String(startDt.getMinutes()).padStart(2, '0')}`,
      type: appointment.type,
      notes: appointment.notes || '',
    });
    setRescheduleDialog({ open: true, appointment });
  };

  const handleReschedule = async () => {
    const { appointment } = rescheduleDialog;
    if (!rescheduleForm.date || !rescheduleForm.time) {
      toast.error('Please select both date and time');
      return;
    }
    setRescheduleLoading(true);
    try {
      const newDatetime = `${rescheduleForm.date}T${rescheduleForm.time}:00`;
      const response = await fetch(`${API_URL}/api/appointments/${appointment.appointment_id}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          start_datetime: newDatetime,
          type: rescheduleForm.type,
          notes: rescheduleForm.notes || null,
        })
      });
      if (response.ok) {
        toast.success('Appointment rescheduled successfully');
        setRescheduleDialog({ open: false, appointment: null });
        fetchAppointments();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to reschedule appointment');
      }
    } catch {
      toast.error('Failed to reschedule appointment');
    } finally {
      setRescheduleLoading(false);
    }
  };

  // ---- Helpers ----
  const navigateDate = (direction) => {
    const d = new Date(selectedDate);
    setSelectedDate((direction === 'next' ? addDays(d, 1) : subDays(d, 1)).toISOString().split('T')[0]);
  };

  const formatTime = (isoString) => {
    try { return format(new Date(isoString), 'h:mm a'); } catch { return isoString; }
  };

  const filteredAppointments = statusFilter === 'all'
    ? appointments
    : appointments.filter(a => a.status === statusFilter);

  const stats = {
    total: appointments.length,
    scheduled: appointments.filter(a => a.status === 'Scheduled').length,
    completed: appointments.filter(a => a.status === 'Completed').length,
    noShow: appointments.filter(a => a.status === 'No Show').length,
    rescheduled: appointments.filter(a => a.status === 'Rescheduled').length,
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  // Confirm dialog messaging
  const getConfirmMessage = (action) => {
    switch (action) {
      case 'Completed':
        return {
          body: 'Mark this consultation as completed?',
          detail: 'The lead will move to "Consultation Completed" stage.',
          detailColor: 'text-emerald-600',
          btnColor: 'bg-emerald-600 hover:bg-emerald-700',
        };
      case 'No Show':
        return {
          body: 'Mark this appointment as No Show?',
          detail: 'The lead will be moved to "Warm Follow-Up" stage. No-show count will be incremented. You can reschedule the appointment afterward.',
          detailColor: 'text-orange-600',
          btnColor: 'bg-orange-600 hover:bg-orange-700',
        };
      case 'Cancelled':
        return {
          body: 'Cancel this appointment?',
          detail: 'The appointment will be marked as cancelled. The lead stage will not change.',
          detailColor: 'text-slate-500',
          btnColor: 'bg-slate-600 hover:bg-slate-700',
        };
      default:
        return { body: `Mark as ${action}?`, detail: '', detailColor: '', btnColor: 'bg-slate-600' };
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-heading">Daily Appointments</h1>
        <p className="text-slate-500 text-sm">Manage consultations and track attendance</p>
      </div>

      {/* Date Navigator + Stats */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateDate('prev')} data-testid="date-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
                data-testid="date-picker"
              />
              {!isToday && (
                <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} data-testid="date-today">
                  Today
                </Button>
              )}
              <Button variant="outline" size="icon" onClick={() => navigateDate('next')} data-testid="date-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex gap-4 text-sm">
                <StatBadge label="Total" value={stats.total} color="text-slate-900" />
                <StatBadge label="Scheduled" value={stats.scheduled} color="text-blue-600" />
                <StatBadge label="Completed" value={stats.completed} color="text-emerald-600" />
                <StatBadge label="No Show" value={stats.noShow} color="text-orange-600" />
                {stats.rescheduled > 0 && <StatBadge label="Rescheduled" value={stats.rescheduled} color="text-violet-600" />}
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appointments Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No appointments for {format(new Date(selectedDate + 'T12:00:00'), 'MMMM d, yyyy')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Rep</TableHead>
                    <TableHead>Status</TableHead>
                    {!isReadOnly && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAppointments.map((appt) => {
                    const TypeIcon = getTypeIcon(appt.type);
                    const statusCfg = STATUS_CONFIG[appt.status] || STATUS_CONFIG.Scheduled;
                    const isScheduled = appt.status === 'Scheduled';
                    const canReschedule = ['No Show', 'Cancelled', 'Scheduled'].includes(appt.status);

                    return (
                      <TableRow key={appt.appointment_id} data-testid={`appointment-row-${appt.appointment_id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            {formatTime(appt.start_datetime)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900">{appt.lead_name}</div>
                          {appt.lead_email && <div className="text-xs text-slate-500">{appt.lead_email}</div>}
                        </TableCell>
                        <TableCell>
                          {appt.lead_phone && (
                            <a href={`tel:${appt.lead_phone}`} className="text-gold hover:underline flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {appt.lead_phone}
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">{appt.type}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{appt.rep_name || 'Unassigned'}</TableCell>
                        <TableCell>
                          <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                          {appt.rescheduled_from && (
                            <span className="ml-1 text-xs text-violet-500">(rescheduled)</span>
                          )}
                        </TableCell>
                        {!isReadOnly && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 flex-wrap">
                              {isScheduled && (
                                <>
                                  <Button
                                    size="sm" variant="outline"
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => setConfirmDialog({ open: true, appointment: appt, action: 'Completed' })}
                                    data-testid={`mark-completed-${appt.appointment_id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" /> Completed
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => setConfirmDialog({ open: true, appointment: appt, action: 'No Show' })}
                                    data-testid={`mark-noshow-${appt.appointment_id}`}
                                  >
                                    <AlertTriangle className="w-4 h-4 mr-1" /> No Show
                                  </Button>
                                </>
                              )}
                              {canReschedule && (
                                <Button
                                  size="sm" variant="outline"
                                  className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                  onClick={() => openReschedule(appt)}
                                  data-testid={`reschedule-${appt.appointment_id}`}
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" /> Reschedule
                                </Button>
                              )}
                              {isScheduled && (
                                <Button
                                  size="sm" variant="ghost"
                                  className="text-slate-500 hover:text-slate-700"
                                  onClick={() => setConfirmDialog({ open: true, appointment: appt, action: 'Cancelled' })}
                                  data-testid={`cancel-${appt.appointment_id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------- Confirm Status Dialog ---------- */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, appointment: null, action: null })}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
          </DialogHeader>
          {(() => {
            const msg = getConfirmMessage(confirmDialog.action);
            return (
              <div className="py-4 space-y-3">
                <p className="text-slate-700">{msg.body}</p>
                {msg.detail && (
                  <div className={`text-sm p-3 rounded-md ${msg.detailColor} bg-opacity-10 border border-current/20`}>
                    <ArrowRight className="w-4 h-4 inline-block mr-1 -mt-0.5" />
                    {msg.detail}
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, appointment: null, action: null })}>
              Cancel
            </Button>
            <Button
              className={getConfirmMessage(confirmDialog.action).btnColor + ' text-white'}
              disabled={actionLoading}
              onClick={() => handleStatusUpdate(confirmDialog.appointment?.appointment_id, confirmDialog.action)}
              data-testid="confirm-action-btn"
            >
              {actionLoading ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Reschedule Dialog ---------- */}
      <Dialog
        open={rescheduleDialog.open}
        onOpenChange={(open) => !open && setRescheduleDialog({ open: false, appointment: null })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-violet-600" />
              Reschedule Appointment
            </DialogTitle>
          </DialogHeader>

          {rescheduleDialog.appointment && (
            <div className="space-y-4 py-2">
              {/* Current appointment info */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <p className="text-slate-500">Current appointment</p>
                <p className="font-medium text-slate-900">{rescheduleDialog.appointment.lead_name}</p>
                <p className="text-slate-600">
                  {formatTime(rescheduleDialog.appointment.start_datetime)} &middot; {rescheduleDialog.appointment.type}
                </p>
                {rescheduleDialog.appointment.status === 'No Show' && (
                  <p className="text-orange-600 text-xs font-medium mt-1">
                    This was a No Show. Rescheduling will move the lead back to "Consultation Scheduled".
                  </p>
                )}
              </div>

              {/* New date */}
              <div className="space-y-2">
                <Label htmlFor="reschedule-date">New Date</Label>
                <Input
                  id="reschedule-date"
                  type="date"
                  value={rescheduleForm.date}
                  onChange={(e) => setRescheduleForm(p => ({ ...p, date: e.target.value }))}
                  data-testid="reschedule-date"
                />
              </div>

              {/* New time */}
              <div className="space-y-2">
                <Label htmlFor="reschedule-time">New Time</Label>
                <Input
                  id="reschedule-time"
                  type="time"
                  value={rescheduleForm.time}
                  onChange={(e) => setRescheduleForm(p => ({ ...p, time: e.target.value }))}
                  data-testid="reschedule-time"
                />
              </div>

              {/* Type */}
              <div className="space-y-2">
                <Label>Appointment Type</Label>
                <Select
                  value={rescheduleForm.type}
                  onValueChange={(v) => setRescheduleForm(p => ({ ...p, type: v }))}
                >
                  <SelectTrigger data-testid="reschedule-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="reschedule-notes">Notes (optional)</Label>
                <Textarea
                  id="reschedule-notes"
                  value={rescheduleForm.notes}
                  onChange={(e) => setRescheduleForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Reason for rescheduling..."
                  rows={2}
                  data-testid="reschedule-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRescheduleDialog({ open: false, appointment: null })}>
              Cancel
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white"
              disabled={rescheduleLoading}
              onClick={handleReschedule}
              data-testid="confirm-reschedule-btn"
            >
              {rescheduleLoading ? 'Rescheduling...' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div className="text-center">
      <p className={`font-bold text-lg ${color}`}>{value}</p>
      <p className="text-slate-500">{label}</p>
    </div>
  );
}
